<template>
  <v-dialog v-model="dialog" width="833" persistent>
    <v-card>
      <v-card-title>
        <span class="text-capitalize">{{ $t('amazon_transparency_connect') }}</span>
        <span>&nbsp;{{ $t('amazon_transparency_product') }}</span>
      </v-card-title>

      <!-- modal body -->
      <div>
        <v-stepper non-linear v-model="step" class="shadow-none">
          <!-- stepper header -->
          <v-stepper-header>
            <v-stepper-step step="1">{{$t('amazon_transparency_select_product')}}</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step step="2" class="text-capitalize">{{$t('amazon_transparency_connect')}}</v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <!--  Step 1: Select product -->
            <v-stepper-content step="1">
                <v-form v-model="isFormValid" class="mx-8 mb-10" ref="productForm">
                    <v-autocomplete
                      :disabled="disableFields"
                      :rules="productRules"
                      :placeholder="productPlaceholder"
                      :hint="$t('amazon_transparency_search_select')"
                      :items="products"
                      :loading="isLoading"
                      :search-input.sync="search"
                      v-model="form.product"
                      append-icon=""
                      hide-details="auto"
                      class="custom-text-field"
                      :item-text="getProductLabel"
                      :hide-no-data="false"
                      :no-data-text="''"
                      @click:clear="onClear"
                      return-object
                      required
                      outlined
                      dense
                      single-line
                      persistent-hint
                      clearable
                      :error-messages="errorMessages.product"
                      @update:search-input="handleSearchInput"
                      @focus="onAutocompleteFocus"
                      @blur="focusStates.productAutoComplete = false"
                      :attach="true"
                    >
                      <!-- No data slot -->
                      <template v-slot:no-data>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title v-if="!isLoading && displayNoData">
                              {{ $t('amazon_transparency_no_products_found') }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-autocomplete>

                  <v-text-field
                    :counter="14"
                    :rules="gtinRules"
                    :placeholder="gtinPlaceholder"
                    :hint="$t('amazon_transparency_search_gtin_registered')"
                    :disabled="disableFields"
                    v-model="form.gtin"
                    hide-details="auto"
                    class="mt-4 custom-text-field"
                    required
                    single-line
                    outlined
                    dense
                    persistent-hint
                    :error-messages="errorMessages.gtin"
                    @focus="focusStates.gtinTextField = true"
                    @blur="focusStates.gtinTextField = false"
                  />

                  <v-textarea
                    :placeholder="remarkPlaceholder"
                    :hint="$t('amazon_transparency_additional_details')"
                    :disabled="disableFields"
                    v-model="form.remark"
                    hide-details="auto"
                    class="mt-4 custom-text-field"
                    single-line
                    outlined
                    dense
                    persistent-hint
                    auto-grow
                    counter="300"
                    :rules="remarkRules"
                    :error-messages="errorMessages.remark"
                    @focus="focusStates.remarkTextArea = true"
                    @blur="focusStates.remarkTextArea = false"
                  />
                </v-form>
            </v-stepper-content>

              <!--  Step 2: Connect -->
              <v-stepper-content step="2">
                  <div class="mx-2" :class="{'mt-4': isConnectionOrConnectionFailed}">
                    <v-row class="pt-0">
                      <v-col>
                        <div class="d-flex align-center" v-if="isConnected || isConnecting">
                          <span class="text-status mr-5">{{ $t('amazon_transparency_connection_status') }}</span>
                          <span class="text-connected text-capitalize" v-if="isConnected">{{ $t('amazon_transparency_connected') }}</span>
                          <span class="text-connecting" v-if="isConnecting">{{ $t('amazon_transparency_connection_progress') }}</span>
                        </div>
                        <div class="d-flex flex-column" v-if="isConnected">
                            <v-switch
                              v-model="isAutoUpload"
                              label=""
                              class="flex-shrink-0 h-35"
                              @change="toggleSwitch"
                            >
                              <template v-slot:label>
                                <span class="ml-4 font-weight-bold black--text">{{ $t('amazon_transparency_auto_upload') }}</span>
                              </template>
                            </v-switch>
                          <div class="d-flex ml-64">
                            <span class="text-wrap text-auto-upload">
                              {{ $t('amazon_transparency_by_enabling') }}
                              "{{ $t('amazon_transparency_auto_upload') }},"
                              {{ $t('amazon_transparency_qrcode_auto_upload') }}
                            </span>
                          </div>
                        </div>
                        <div class="d-flex align-center" v-if="isConnectionFailed">
                          <div class="d-flex">
                            <span class="text-status mr-5">{{ $t('amazon_transparency_connection_status') }}</span>
                            <div class="d-flex flex-column">
                              <div class="d-flex align-center">
                                <span class="text-failed text-capitalize">{{ $t('amazon_transparency_failed') }}</span>
                              </div>
                              <span class="text-error mt-1">{{ failedErrorMessage }}</span>
                            </div>
                          </div>
                          <v-spacer></v-spacer>
                          <v-btn class="text-uppercase mb-6" color="primary" @click="handleReconnectBtnClick" :loading="isReconnecting">{{ $t('amazon_transparency_reconnect') }}</v-btn>
                        </div>
                      </v-col>
                    </v-row>
                    <v-divider class="mt-4" v-if="isConnectionOrConnectionFailed || isConnecting"></v-divider>
                    <v-row :class="{'mt-4': isConnectionOrConnectionFailed || isConnecting}">
                    <!-- first row -->
                      <v-col>
                        <div class="d-flex">
                          <span class="mr-10 font-weight-bold text-capitalize w-80 text-bold flex-shrink-0" :class="greyTextColor">{{ $t('amazon_transparency_product') }}</span>
                          <span :class="greyTextColor" class="text-wrap">{{ form.product?.name }}</span>
                        </div>
                      </v-col>
                      <v-col>
                        <div class="d-flex">
                          <span class="font-weight-bold text-capitalize text-bold w-80 flex-shrink-0 mr-2" :class="greyTextColor">{{ $t('amazon_transparency_sku') }}</span>
                          <span :class="greyTextColor">{{ form.product?.sku }}</span>
                        </div>
                      </v-col>
                    </v-row>

                    <!-- second row -->
                    <v-row>
                      <v-col>
                        <div class="d-flex">
                          <span class="mr-10 font-weight-bold text-capitalize text-bold w-80 flex-shrink-0" :class="greyTextColor">{{ $t('amazon_transparency_gtin_14') }}</span>
                          <span :class="greyTextColor">{{ form.gtin }}</span>
                        </div>
                      </v-col>
<!--                      <v-col>-->
<!--                        <div class="d-flex">-->
<!--                          <span class="mr-10 font-weight-bold w-145 text-bold" :class="greyTextColor">{{ $t('amazon_transparency_total_active_codes') }}</span>-->
<!--                          <span :class="greyTextColor">{{ form.product?.code_count }}</span>-->
<!--                        </div>-->
<!--                      </v-col>-->
                    </v-row>

                    <!-- third row -->
                    <v-row v-if="form.remark">
                      <v-col>
                        <div class="d-flex">
                          <span class="mr-10 font-weight-bold text-capitalize text-bold w-80 flex-shrink-0" :class="greyTextColor">{{ $t('amazon_transparency_remark') }}</span>
                          <span :class="greyTextColor">{{ form.remark }}</span>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
              </v-stepper-content>
          </v-stepper-items>
        </v-stepper>

      </div>
      <!-- modal actions -->
      <v-card-actions class="pa-4 px-8 mt-3 pb-6">
        <div v-if="step === 2">
          <v-btn class="text-uppercase mr-4" text color="primary" @click="handleBackBtnClick" v-if="(!isConnected && !isConnecting) || isRegisteringProduct" :disabled="isRegisteringProduct">{{ $t('amazon_transparency_back') }}</v-btn>
          <v-btn class="text-uppercase mr-4" text color="primary" @click="handleConnectAnotherProduct" v-if="(isConnected || isConnecting) && !isRegisteringProduct">{{ $t('amazon_transparency_connect_another_product') }}</v-btn>
        </div>
        <v-spacer></v-spacer>
        <div v-if="step === 1">
          <v-btn class="text-uppercase mr-4" text color="primary" @click="closeModal" v-if="step === 1">{{ $t('amazon_transparency_cancel') }}</v-btn>
          <v-btn class="text-uppercase" color="primary" @click="handleNextBtnClick" :loading="isConnecting" :disabled="!isFormValid">{{ $t('amazon_transparency_next') }}</v-btn>
        </div>
        <div v-if="step === 2">
          <v-btn
            class="text-uppercase"
            color="primary"
            @click="handleConnectBtnClick"
            v-if="!isConnected && !isConnectionFailed && !isConnecting"
            :loading="isConnecting"
          >
            {{ $t('amazon_transparency_connect') }}
          </v-btn>
          <v-btn class="text-uppercase" color="primary" @click="closeModal" v-if="isConnected || isConnectionFailed || isConnecting">{{ $t('amazon_transparency_close') }}</v-btn>
        </div>
      </v-card-actions>
    </v-card>
    <auto-upload-confirmation-modal
      :display-confirmation-modal="showConfirmationModal"
      :handle-cancel-btn="handleCancelBtn"
      :handle-disable-btn="handleDisableBtn"
      :handle-enable-btn="handleEnableBtn"
      :is-disabling="isDisablingAutoUpload"/>
  </v-dialog>
</template>

<script>
import AutoUploadConfirmationModal from '@/components/amazon-transparency/dialog/AutoUploadConfirmationModal.vue'
import utils from '@/utils/utils'
import { mapGetters } from 'vuex'

export default {
  name: 'ConnectProductDialog',
  components: {
    AutoUploadConfirmationModal
  },
  props: {
    displayConnectProductDialog: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
      step: 1,
      isFormValid: false,
      isConnecting: false,
      isReconnecting: false,
      isRegisteringProduct: false,
      failedErrorMessage: '',
      isLoading: false,
      search: null,
      form: {
        gtin: '',
        product: null,
        remark: ''
      },
      isConnectionFailed: false,
      initialProducts: [],
      errorMessages: {
        gtin: '',
        product: '',
        remark: ''
      },
      pollingInterval: null,
      isAutoUpload: false,
      showConfirmationModal: false,
      isDisablingAutoUpload: false,
      focusStates: {
        productAutoComplete: false,
        gtinTextField: false,
        remarkTextArea: false
      },
      isconnectingAnotherProduct: false // for tracking local modal state
    }
  },
  computed: {
    ...mapGetters(['products', 'filteredProductCount', 'newATPProduct', 'getATPConfig', 'isNewATPProductConnected', 'atpProducts', 'isConnectingAnotherProductOrPolling', 'requesterParty']),
    accountId () {
      return this.getATPConfig.account_id
    },
    dialog: {
      get () {
        return this.displayConnectProductDialog
      },
      set (value) {
        this.$emit('update:displayConnectProductDialog', value)
      }
    },
    productRules () {
      return [
        v => !!v || this.$t('amazon_transparency_required')
      ]
    },
    gtinRules () {
      return [
        v => !!v || this.$t('amazon_transparency_required'),
        v => /^[0-9]+$/.test(v) || this.$t('amazon_transparency_numeric_allowed'),
        v => (v && v.length === 14) || this.$t('amazon_transparency_gtin_fourteen_characters')
      ]
    },
    remarkRules () {
      return [
        v => (v || '').length <= 300 || this.$t('maximum_characters_allowed', { count: 300 })
      ]
    },
    disableFields () {
      return this.isConnecting
    },
    isConnectionOrConnectionFailed () {
      return this.isConnectionFailed || this.isConnected
    },
    greyTextColor () {
      return {
        'grey--text text--lighten-1': this.isConnectionOrConnectionFailed
      }
    },
    displayNoData () {
      return this.products.length === 0
    },
    isConnected () {
      if (this.isConnectingAnotherProductOrPolling) return false
      return this.isNewATPProductConnected
    },
    gtinPlaceholder () {
      return this.focusStates.gtinTextField ? '' : this.$t('amazon_transparency_gtin_field')
    },
    productPlaceholder () {
      return this.focusStates.productAutoComplete ? '' : this.$t('amazon_transparency_product_field')
    },
    remarkPlaceholder () {
      return this.focusStates.remarkTextArea ? '' : this.$t('amazon_transparency_remark_field')
    }
  },
  methods: {
    resetFocusStates () {
      this.focusStates = {
        gtinTextField: false,
        productAutoComplete: false,
        remarkTextArea: false
      }
    },
    resetDialog () {
      this.form = {
        gtin: '',
        product: '',
        remark: ''
      }
      this.isConnectionFailed = false
      this.search = null
      this.isConnecting = false
      this.isLoading = false
      this.step = 1
      this.initialProducts = []
      this.isAutoUpload = false
      this.errorMessages = {
        gtin: '',
        product: '',
        remark: ''
      }
      this.resetFocusStates()
      if (this.isRegisteringProduct) this.isRegisteringProduct = false

      this.$nextTick(() => {
        if (this.$refs.productForm) {
          this.$refs.productForm.resetValidation()
        }
      })
      if (this.pollingInterval === null && this.newATPProduct?.status === 'connected') {
        this.stopPolling()
        this.resetProduct()
        this.$store.dispatch('updateNewProductConnectedStatus', 'completed')
      }
    },
    resetProduct () {
      this.$store.dispatch('resetNewATPProduct')
    },
    closeModal () {
      this.dialog = false
      this.resetDialog()
      if (this.pollingInterval && this.newATPProduct && this.newATPProduct?.status !== 'connected') {
        this.updateIsPollingOrConnectingStatus(this.newATPProduct?.id, true)
      }
      this.isconnectingAnotherProduct = false
    },
    async startPolling () {
      if (this.pollingInterval) return
      this.pollingInterval = setInterval(this.checkVerificationStatus, 4000)
    },
    async stopPolling () {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
        if (this.isConnecting) this.isConnecting = false
      }
    },
    handleBackBtnClick () {
      this.moveToStep1()
    },
    moveToStep1 () {
      if (this.step === 2) {
        this.step--
      }
    },
    moveToStep2 () {
      if (this.step === 1) {
        this.step++
      }
    },
    processErrorMessages (error) {
      if (error.response) {
        const errorMessages = error.response.data
        this.errorMessages = {
          gtin: '',
          product: '',
          remark: ''
        }

        const extractMessages = (obj, parentKey = '') => {
          for (const key in obj) {
            if (key === 'detail') {
              this.errorMessages.product = obj[key]
            } else if (Array.isArray(obj[key])) {
              const mappedKey = {
                gtin: 'gtin',
                product: 'product',
                remark: 'remark'
              }[key]

              // get values for numeric keys
              const fieldName = isNaN(key) ? mappedKey : parentKey

              if (this.errorMessages[fieldName] !== undefined) {
                this.errorMessages[fieldName] = obj[key].join(' ')
              }
            } else if (typeof obj[key] === 'string') {
              if (this.errorMessages[key] !== undefined) {
                this.errorMessages[key] = obj[key]
              }
            } else if (typeof obj[key] === 'object' && obj[key] !== null) {
              extractMessages(obj[key], isNaN(key) ? key : parentKey)
            }
          }
        }

        extractMessages(errorMessages)
      }
    },
    async registerAndConnectProduct () {
      try {
        if ((utils.isDevEnvironment() || utils.isStagingEnvironment()) && !this.requesterParty) {
          await this.$store.dispatch('showSnackBar', { message: this.$t('amazon_transparency_party_not_found') })
          this.moveToStep1()
          return
        }

        this.isRegisteringProduct = true
        const payload = {
          productId: this.form.product.id,
          gtin: this.form.gtin
        }

        await this.$store.dispatch('createATPProduct', payload)
        if (this.form.remark) {
          const descriptionPayload = { description: this.form.remark }
          descriptionPayload.productId = this.newATPProduct?.id

          await this.$store.dispatch('updateATPProduct', descriptionPayload)
        }
        if (utils.isDevEnvironment() || utils.isStagingEnvironment()) {
          await this.$store.dispatch('updateRequesterPartyGTINS', { gln: this.accountId, gtins: [this.form.gtin] })
        }
        this.$refs.productForm.resetValidation()

        // only connect product after product registration
        await this.connectProduct()
      } catch (error) {
        this.moveToStep1()
        if (error) {
          this.processErrorMessages(error)
        }
      } finally {
        this.isRegisteringProduct = false
      }
    },
    async connectProduct () {
      try {
        this.isConnecting = true
        if (this.isconnectingAnotherProduct) this.isconnectingAnotherProduct = false
        const productId = this.newATPProduct?.id
        await this.$store.dispatch('verifyATPProduct', { productId })
        await this.startPolling()
      } catch (error) {
        this.isConnectionFailed = true
        await this.stopPolling()
        if (error.response && error.response.data) {
          this.failedErrorMessage = error.response?.data?.detail
        } else {
          await this.$store.dispatch('showSnackBar', { message: error?.message })
        }
      }
    },
    async handleNextBtnClick () {
      this.moveToStep2()
    },
    async handleConnectBtnClick () {
      await this.registerAndConnectProduct()
    },
    async handleReconnectBtnClick () {
      try {
        this.isReconnecting = true
        const payload = { productId: this.newATPProduct?.id }
        await this.$store.dispatch('reconnectATPProduct', payload)
        this.isReconnecting = false
      } catch (error) {
        this.isReconnecting = false
        if (error.response && error.response.data) {
          this.failedErrorMessage = error.response?.data?.detail
        } else {
          await this.$store.dispatch('showSnackBar', { message: error?.message })
        }
      }
    },
    handleConnectAnotherProduct () {
      this.isconnectingAnotherProduct = true
      this.resetDialog()
      if (this.pollingInterval && this.newATPProduct && this.newATPProduct?.status !== 'connected') {
        this.updateIsPollingOrConnectingStatus(this.newATPProduct?.id, true)
      }
    },
    getProductLabel (product) {
      return `${product?.name} - ${product?.sku}`
    },
    async fetchProducts () {
      this.isLoading = true
      try {
        const params = {
          limit: 20,
          offset: 0,
          is_archived: false,
          ordering: '-id'
        }

        if (this.search) params.search = this.search

        await this.$store.dispatch('loadProducts', params)
      } catch (error) {
        console.error('Error fetching products:', error)
      } finally {
        this.isLoading = false
      }
    },
    async onClear () {
      this.search = ''
      this.form.product = null
      if (this.initialProducts.length > 0) {
        const payload = { products: this.initialProducts, count: this.initialProducts.length }
        this.$store.commit('SET_PRODUCT_LIST', { ...payload })
      } else {
        await this.fetchProducts()
      }
    },
    async checkVerificationStatus () {
      try {
        const productId = this.newATPProduct?.id
        await this.$store.dispatch('verifyATPProduct', { productId })

        // modal closed and polling running in the background
        if (!this.displayConnectProductDialog) {
          const product = this.atpProducts.find(atpProduct => atpProduct.id === productId)
          if (product && product.status === 'connected') {
            await this.stopPolling()
          }
        } else {
          // modal visible, newATPProduct status gets reflected
          const status = this.newATPProduct?.status
          if (status === 'connected') {
            this.isAutoUpload = this.newATPProduct?.is_active
            await this.stopPolling()
          }
        }
      } catch (error) {
        if (error.response && error.response.data) {
          this.failedErrorMessage = error.response?.data?.detail
        } else {
          await this.$store.dispatch('showSnackBar', { message: error?.message })
        }
        await this.stopPolling()
        this.isConnectionFailed = true
      }
    },
    toggleSwitch (val) {
      if (val) {
        this.showConfirmationModal = true
        this.isDisablingAutoUpload = false
      } else {
        this.showConfirmationModal = true
        this.isDisablingAutoUpload = true
      }
    },
    handleCancelBtn () {
      this.isAutoUpload = this.newATPProduct?.is_active
      this.showConfirmationModal = false
    },
    handleEnableBtn () {
      this.showConfirmationModal = false
      this.enableAutoUpload()
    },
    handleDisableBtn () {
      this.showConfirmationModal = false
      this.disableAutoUpload()
    },
    async disableAutoUpload () {
      try {
        const payload = { productId: this.newATPProduct.id }
        await this.$store.dispatch('deactivateATPProduct', payload)

        const snackbarPayload = { message: this.$t('amazon_transparency_product_auto_upload_disabled') }
        await this.$store.dispatch('showSnackBar', snackbarPayload)
      } catch (error) {
        if (error.response && error.response.data) {
          const snackbarPayload = { message: error.response?.data?.detail }
          await this.$store.dispatch('showSnackBar', snackbarPayload)
        } else {
          await this.$store.dispatch('showSnackBar', { message: error?.message })
        }
      }
    },
    async enableAutoUpload () {
      try {
        const payload = { productId: this.newATPProduct.id }
        await this.$store.dispatch('activateATPProduct', payload)

        const snackbarPayload = { message: this.$t('amazon_transparency_product_auto_upload_enabled') }
        await this.$store.dispatch('showSnackBar', snackbarPayload)
      } catch (error) {
        if (error.response && error.response.data) {
          const snackbarPayload = { message: error.response?.data?.detail }
          await this.$store.dispatch('showSnackBar', snackbarPayload)
        } else {
          await this.$store.dispatch('showSnackBar', { message: error?.message })
        }
      }
    },
    updateIsPollingOrConnectingStatus (productId = null, status = false) {
      const payload = {
        productId,
        isConnectingOrPolling: status
      }
      this.$store.dispatch('setIsConnectingAnotherProductOrPolling', payload)
    },
    handleSearchInput (val) {
      if (val) {
        const isMultipleSpacesOnly = /^\s{2,}$/.test(val)
        if (isMultipleSpacesOnly) {
          this.search = ''
        }
      }
    },
    async onAutocompleteFocus () {
      this.focusStates.productAutoComplete = true
      if (this.products.length === 0 && this.initialProducts.length > 0) {
        await this.fetchProducts()
      }
    }
  },
  created () {
    this.debouncedSearch = utils.debounce(() => {
      this.fetchProducts()
    }, 400, false)
  },
  watch: {
    search (val) {
      if (val && val !== this.getProductLabel(this.form.product)) {
        this.debouncedSearch()
      }
    },
    async displayConnectProductDialog (newVal) {
      if (newVal) {
        // always reset product connected state
        await this.$store.dispatch('updateNewProductConnectedStatus', 'completed')
        const payload = {
          campaign: null,
          limit: 20,
          offset: 0,
          is_archived: false,
          ordering: '-id'
        }
        await this.$store.dispatch('loadProducts', payload)
        this.initialProducts = this.products
      }
    },
    'form.product' (newValue) {
      if (newValue && this.errorMessages.product) {
        this.errorMessages.product = ''
      }
    },
    'form.gtin' (newValue) {
      if (newValue && this.errorMessages.gtin) {
        this.errorMessages.gtin = ''
      }
    },
    'form.remark' (newValue) {
      if (newValue && this.errorMessages.remark) {
        this.errorMessages.remark = ''
      }
    },
    isNewATPProductConnected (newVal) {
      if (newVal && this.isConnectingAnotherProductOrPolling) {
        let updatedProduct = { ...this.newATPProduct }
        if (updatedProduct?.status?.toLowerCase() === 'in progress') {
          updatedProduct = {
            ...updatedProduct,
            status: 'connected'
          }
        }
        this.$store.dispatch('setUpdateConnectedAtpProduct', updatedProduct)
        if (this.isConnectionFailed) this.isConnectionFailed = false
        this.stopPolling()
        // Do not reset the product when the modal is open and the user is on step 2 polling.
        // This shows the connected status after connecting.
        if (this.displayConnectProductDialog !== true) {
          this.$store.dispatch('updateNewProductConnectedStatus', 'completed')
          this.resetProduct()
        }

        if (!this.isconnectingAnotherProduct) {
          this.updateIsPollingOrConnectingStatus(null, false)
        }
      }
    }
  }
}
</script>

<style lang="sass" scoped>
  .shadow-none
    box-shadow: none !important

  .text-bold
    color: #000
    font-size: 16px
    font-style: normal
    font-weight: 700
    line-height: 26px
    width: 80px !important

  .w-80
    width: 80px !important

  .w-145
    width: 145px !important

  .w-183
    width: 183px !important

  .text-status
    color: #000
    font-size: 18px
    font-style: normal
    font-weight: 700
    line-height: 26px

  .text-connected
    color: #3FC135
    font-size: 18px
    font-style: normal
    font-weight: 700
    line-height: 20px
    letter-spacing: 0.25px

  .text-connecting
    color: #999
    font-size: 18px
    font-style: normal
    font-weight: 700
    line-height: 20px
    letter-spacing: 0.25px

  .text-failed
    color: #FA4C5B
    font-size: 18px
    font-style: normal
    font-weight: 700
    line-height: 26px
    letter-spacing: 0.25px

  .text-error
    color: #000
    font-size: 18px
    font-style: normal
    font-weight: 400
    line-height: 26px

  .ml-64
    margin-left: 64px !important

  .h-25
    height: 35px !important

  ::v-deep .custom-text-field
    .v-input__control input::placeholder,
    .v-input__control textarea::placeholder
      color: black !important

    .v-messages
      margin-left: 0 !important
      padding-left: 0 !important

      &__message
        text-align: left !important
        margin-left: 0 !important
        padding-left: 0 !important

    &.v-input
     .v-text-field__details
       padding-left: 0 !important

       .v-messages__message
         margin-left: 0 !important

</style>
